import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const AdmissionsOffice = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('applications');

  const sections = [
    { id: 'applications', label: '📋 Applications' },
    { id: 'onboarding', label: '✅ Onboarding Checklist' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button style={styles.returnBtn} onClick={() => navigate('/campus')}>
            ← Return to Campus Square
          </button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.pageHeader}>
          <h2 style={styles.pageTitle}>📋 Admissions Office</h2>
          <p style={styles.pageSubtitle}>Review applications and manage onboarding</p>
        </div>

        <div style={styles.tabs}>
          {sections.map(section => (
            <button
              key={section.id}
              style={activeSection === section.id ? styles.tabActive : styles.tab}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div style={styles.content}>
          {activeSection === 'applications' && <Applications user={user} />}
          {activeSection === 'onboarding' && <OnboardingBuilder user={user} />}
        </div>
      </div>
    </div>
  );
};

// ==================== APPLICATIONS ====================
const Applications = ({ user }) => {
  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const isAdmin = user?.role_id === 1;

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  useEffect(() => {
    if (selected) setNotes(selected.notes || '');
  }, [selected]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admissions/applications?status=${filter}`
      );
      setApplications(res.data.applications);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await axios.patch(
        `${API_URL}/api/admissions/applications/${selected.id}`,
        { status, notes }
      );
      setMessage(status === 'approved' ? 
        'Application approved. Approval email sent.' : 
        'Application rejected.'
      );
      setSelected(null);
      fetchApplications();
    } catch (err) {
      setMessage('Error updating application.');
    }
  };

  const statusColor = (status) => {
    if (status === 'approved') return '#1E8449';
    if (status === 'rejected') return '#922B21';
    return '#C49A28';
  };

  const statusBg = (status) => {
    if (status === 'approved') return '#1E844920';
    if (status === 'rejected') return '#922B2120';
    return '#C49A2820';
  };

  return (
    <div style={styles.applicationsContainer}>
      {/* Filter Bar */}
      <div style={styles.filterBar}>
        {['pending', 'approved', 'rejected'].map(s => (
          <button
            key={s}
            style={filter === s ? styles.filterBtnActive : styles.filterBtn}
            onClick={() => { setFilter(s); setSelected(null); }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {message && (
        <div style={styles.messageBox}>
          {message}
          <button style={styles.dismissBtn} onClick={() => setMessage('')}>×</button>
        </div>
      )}

      <div style={styles.splitView}>
        {/* Applications List */}
        <div style={styles.appList}>
          {loading && <p style={styles.loadingText}>Loading...</p>}
          {!loading && applications.length === 0 && (
            <p style={styles.emptyText}>No {filter} applications.</p>
          )}
          {applications.map(app => (
            <div
              key={app.id}
              style={selected?.id === app.id ? styles.appItemActive : styles.appItem}
              onClick={() => setSelected(app)}
            >
              <div style={styles.appItemTop}>
                <p style={styles.appName}>{app.first_name} {app.last_name}</p>
                <span style={{
                  ...styles.statusBadge,
                  color: statusColor(app.status),
                  backgroundColor: statusBg(app.status),
                }}>
                  {app.status}
                </span>
              </div>
              <p style={styles.appProfession}>{app.profession}</p>
              <p style={styles.appDate}>
                {new Date(app.submitted_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric'
                })}
              </p>
            </div>
          ))}
        </div>

        {/* Application Detail */}
        <div style={styles.appDetail}>
          {!selected && (
            <div style={styles.noSelection}>
              <p style={styles.noSelectionText}>Select an application to review</p>
            </div>
          )}

          {selected && (
            <>
              <div style={styles.detailHeader}>
                <div>
                  <h3 style={styles.detailName}>
                    {selected.first_name} {selected.last_name}
                  </h3>
                  <p style={styles.detailEmail}>{selected.email}</p>
                  {selected.phone && (
                    <p style={styles.detailPhone}>{selected.phone}</p>
                  )}
                </div>
                <span style={{
                  ...styles.statusBadgeLarge,
                  color: statusColor(selected.status),
                  backgroundColor: statusBg(selected.status),
                  border: `1px solid ${statusColor(selected.status)}40`,
                }}>
                  {selected.status}
                </span>
              </div>

              <div style={styles.detailSection}>
                <h4 style={styles.detailLabel}>Profession</h4>
                <p style={styles.detailText}>{selected.profession}</p>
              </div>

              <div style={styles.detailSection}>
                <h4 style={styles.detailLabel}>Why they want to join</h4>
                <p style={styles.detailText}>{selected.why_joining}</p>
              </div>

              <div style={styles.detailSection}>
                <h4 style={styles.detailLabel}>Goals</h4>
                <p style={styles.detailText}>{selected.goals}</p>
              </div>

              {selected.heard_about && (
                <div style={styles.detailSection}>
                  <h4 style={styles.detailLabel}>How they heard about IIA</h4>
                  <p style={styles.detailText}>{selected.heard_about}</p>
                </div>
              )}

              <div style={styles.detailSection}>
                <h4 style={styles.detailLabel}>Submitted</h4>
                <p style={styles.detailText}>
                  {new Date(selected.submitted_at).toLocaleString()}
                </p>
              </div>

              <div style={styles.detailSection}>
                <h4 style={styles.detailLabel}>Internal Notes</h4>
                <textarea
                  style={styles.notesArea}
                  placeholder="Add notes about this applicant..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {isAdmin && selected.status === 'pending' && (
                <div style={styles.decisionBtns}>
                  <button
                    style={styles.approveBtn}
                    onClick={() => handleUpdateStatus('approved')}
                  >
                    ✓ Approve & Send Email
                  </button>
                  <button
                    style={styles.rejectBtn}
                    onClick={() => handleUpdateStatus('rejected')}
                  >
                    ✗ Reject
                  </button>
                </div>
              )}

              {selected.status !== 'pending' && (
                <div style={styles.reviewedNote}>
                  <p style={styles.reviewedText}>
                    Reviewed on {new Date(selected.reviewed_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== ONBOARDING BUILDER ====================
const OnboardingBuilder = ({ user }) => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', is_required: true });
  const [message, setMessage] = useState('');

  const isAdmin = user?.role_id === 1;

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admissions/onboarding`);
      setItems(res.data.items);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/admissions/onboarding`, {
        ...form,
        order_index: items.length,
      });
      setMessage('Item added');
      setShowForm(false);
      setForm({ title: '', description: '', is_required: true });
      fetchItems();
    } catch (err) {
      setMessage('Error adding item');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/admissions/onboarding/${itemId}`);
      fetchItems();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div style={styles.onboardingHeader}>
        <div>
          <h3 style={styles.sectionTitle}>Onboarding Checklist</h3>
          <p style={styles.sectionSubtitle}>
            Steps every new student must complete after approval
          </p>
        </div>
        {isAdmin && (
          <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>
            + Add Item
          </button>
        )}
      </div>

      {message && <p style={styles.successText}>{message}</p>}

      {showForm && (
        <div style={styles.itemForm}>
          <input
            style={styles.input}
            placeholder="Checklist item title"
            value={form.title}
            onChange={e => setForm({...form, title: e.target.value})}
          />
          <input
            style={styles.input}
            placeholder="Description (optional)"
            value={form.description}
            onChange={e => setForm({...form, description: e.target.value})}
          />
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={form.is_required}
              onChange={e => setForm({...form, is_required: e.target.checked})}
            />
            <span style={styles.checkboxText}>Required</span>
          </label>
          <div style={styles.formActions}>
            <button style={styles.saveBtn} onClick={handleCreate}>Save Item</button>
            <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {items.length === 0 && !showForm && (
        <p style={styles.emptyText}>No onboarding items yet.</p>
      )}

      <div style={styles.itemsList}>
        {items.map((item, index) => (
          <div key={item.id} style={styles.itemRow}>
            <div style={styles.itemLeft}>
              <span style={styles.itemNumber}>{index + 1}</span>
              <div>
                <p style={styles.itemTitle}>
                  {item.title}
                  {item.is_required && (
                    <span style={styles.requiredBadge}>Required</span>
                  )}
                </p>
                {item.description && (
                  <p style={styles.itemDesc}>{item.description}</p>
                )}
              </div>
            </div>
            {isAdmin && (
              <button
                style={styles.deleteItemBtn}
                onClick={() => handleDelete(item.id)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#1A2744', color: '#ffffff' },
  header: { backgroundColor: '#0f1a33', borderBottom: '1px solid #C49A28', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { color: '#C49A28', fontSize: '24px', fontWeight: '800', margin: '0', letterSpacing: '2px' },
  logoText: { color: '#ffffff80', fontSize: '14px', letterSpacing: '1px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  welcome: { color: '#ffffff', fontSize: '14px' },
  returnBtn: { backgroundColor: 'transparent', border: '1px solid #C49A28', color: '#C49A28', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  main: { maxWidth: '1200px', margin: '0 auto', padding: '32px' },
  pageHeader: { marginBottom: '24px' },
  pageTitle: { color: '#C49A28', fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0' },
  pageSubtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #ffffff15' },
  tab: { backgroundColor: 'transparent', border: 'none', color: '#ffffff60', padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid transparent' },
  tabActive: { backgroundColor: 'transparent', border: 'none', color: '#C49A28', padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid #C49A28' },
  content: { minHeight: '400px' },
  applicationsContainer: { display: 'flex', flexDirection: 'column', gap: '16px' },
  filterBar: { display: 'flex', gap: '8px' },
  filterBtn: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', color: '#ffffff60', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  filterBtnActive: { backgroundColor: '#C49A28', border: '1px solid #C49A28', color: '#1A2744', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
  messageBox: { backgroundColor: '#1E844920', border: '1px solid #1E844940', borderRadius: '8px', padding: '12px 16px', color: '#1E8449', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dismissBtn: { backgroundColor: 'transparent', border: 'none', color: '#1E8449', fontSize: '18px', cursor: 'pointer' },
  splitView: { display: 'grid', gridTemplateColumns: '300px 1fr', gap: '0', minHeight: '500px' },
  appList: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px 0 0 10px', overflowY: 'auto', maxHeight: '600px' },
  appItem: { padding: '16px', borderBottom: '1px solid #ffffff08', cursor: 'pointer' },
  appItemActive: { padding: '16px', borderBottom: '1px solid #ffffff08', cursor: 'pointer', backgroundColor: '#1A2744' },
  appItemTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  appName: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: '0' },
  statusBadge: { fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  appProfession: { color: '#ffffff60', fontSize: '12px', margin: '0 0 2px 0' },
  appDate: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  appDetail: { backgroundColor: '#0d1628', border: '1px solid #ffffff15', borderLeft: 'none', borderRadius: '0 10px 10px 0', padding: '28px', overflowY: 'auto', maxHeight: '600px' },
  noSelection: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
  noSelectionText: { color: '#ffffff30', fontSize: '14px' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #ffffff10' },
  detailName: { color: '#ffffff', fontSize: '20px', fontWeight: '700', margin: '0 0 4px 0' },
  detailEmail: { color: '#C49A28', fontSize: '13px', margin: '0 0 2px 0' },
  detailPhone: { color: '#ffffff60', fontSize: '13px', margin: '0' },
  statusBadgeLarge: { fontSize: '12px', fontWeight: '700', padding: '6px 14px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '1px' },
  detailSection: { marginBottom: '20px' },
  detailLabel: { color: '#ffffff40', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 6px 0' },
  detailText: { color: '#ffffffcc', fontSize: '14px', lineHeight: '1.7', margin: '0' },
  notesArea: { width: '100%', backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  decisionBtns: { display: 'flex', gap: '10px', marginTop: '8px' },
  approveBtn: { flex: 1, backgroundColor: '#1E8449', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  rejectBtn: { backgroundColor: 'transparent', border: '1px solid #922B2140', color: '#E74C3C', borderRadius: '8px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  reviewedNote: { marginTop: '16px', padding: '12px', backgroundColor: '#ffffff08', borderRadius: '6px' },
  reviewedText: { color: '#ffffff40', fontSize: '12px', margin: '0' },
  onboardingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  sectionTitle: { color: '#ffffff', fontSize: '18px', fontWeight: '700', margin: '0 0 4px 0' },
  sectionSubtitle: { color: '#ffffff60', fontSize: '13px', margin: '0' },
  addBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  itemForm: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px', padding: '20px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' },
  input: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '14px', outline: 'none', boxSizing: 'border-box', width: '100%' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
  checkboxText: { color: '#ffffff80', fontSize: '13px' },
  formActions: { display: 'flex', gap: '8px' },
  saveBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff30', color: '#ffffff60', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer' },
  itemsList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  itemRow: { backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  itemLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  itemNumber: { width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#1A6B8A', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', flexShrink: 0 },
  itemTitle: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: '0 0 2px 0' },
  requiredBadge: { marginLeft: '8px', backgroundColor: '#C49A2820', color: '#C49A28', fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  itemDesc: { color: '#ffffff60', fontSize: '12px', margin: '0' },
  deleteItemBtn: { backgroundColor: 'transparent', border: '1px solid #ff000030', color: '#ff6666', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  loadingText: { color: '#ffffff60', fontSize: '14px', padding: '16px' },
  emptyText: { color: '#ffffff30', fontSize: '14px', padding: '16px' },
  successText: { color: '#1E8449', fontSize: '13px', margin: '0 0 12px 0' },
};

export default AdmissionsOffice;